import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';

export type CampaignGroupCampaign = {
  id: string;
  name: string;
};

export type CampaignGroup = {
  id: string;
  groupName: string;
  created: string;
  campaignList: CampaignGroupCampaign[];
};

type CampaignGroupsResponse = {
  data: CampaignGroup[];
};

type CampaignGroupResponse = {
  data: CampaignGroup;
};

const campaignGroupsQueryKey = (customer: string) =>
  ['useCampaignGroupsQuery', customer] as const;

export function useCampaignGroupsQuery(customer: string) {
  const queryId = useMemo(() => campaignGroupsQueryKey(customer), [customer]);

  return useQuery(
    queryId,
    async () => {
      const response = await request.get<CampaignGroupsResponse>(
        '/data/campaign-groups',
        {
          params: { customer },
        },
      );
      return response.data?.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      enabled: Boolean(customer),
    },
  );
}

type CreateCampaignGroupVariables = {
  groupName: string;
  campaignIds: number[];
};

export function useCreateCampaignGroupMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (variables: CreateCampaignGroupVariables) => {
      const response = await request.post<CampaignGroupResponse>(
        '/data/campaign-groups',
        variables,
        {
          params: { customer },
        },
      );
      return response.data?.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(campaignGroupsQueryKey(customer));
      },
    },
  );
}

type UpdateCampaignGroupVariables = {
  groupId: string;
  groupName: string;
  campaignIds: number[];
};

export function useUpdateCampaignGroupMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      groupId,
      groupName,
      campaignIds,
    }: UpdateCampaignGroupVariables) => {
      const response = await request.put<CampaignGroupResponse>(
        `/data/campaign-groups/${groupId}`,
        {
          groupName,
          campaignIds,
        },
        {
          params: { customer },
        },
      );
      return response.data?.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(campaignGroupsQueryKey(customer));
      },
    },
  );
}

type DeleteCampaignGroupVariables = {
  groupId: string;
};

export function useDeleteCampaignGroupMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ groupId }: DeleteCampaignGroupVariables) => {
      const response = await request.delete<{ data: boolean }>(
        `/data/campaign-groups/${groupId}`,
        {
          params: { customer },
        },
      );
      return response.data?.data ?? false;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(campaignGroupsQueryKey(customer));
      },
    },
  );
}
